import * as RadixDialog from "@radix-ui/react-dialog";
import { Command } from "cmdk";
import { CornerDownLeft, LogOut, Search } from "lucide-react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router";

import { useCan, usePermissions } from "@/app/session/permissions";
import { useSession } from "@/app/session/useSession";
import { CREATE_ACTIONS, createHref } from "@/app/shell/createActions";
import { NAV_ENTRIES, type NavEntry } from "@/app/shell/nav";
import { PaletteRecords } from "@/app/shell/PaletteRecords";
import { useT } from "@/shared/i18n/useT";
import { cn } from "@/shared/lib/cn";
import { Kbd } from "@/shared/ui/Kbd";

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const GROUP =
  "[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:pb-1.5 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:tracking-[0.12em] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:text-ink-subtle";

export function PaletteItem({
  value,
  keywords,
  onSelect,
  children,
}: {
  value: string;
  keywords?: string[];
  onSelect: () => void;
  children: ReactNode;
}) {
  return (
    <Command.Item
      value={value}
      keywords={keywords}
      onSelect={onSelect}
      className={cn(
        "flex min-h-10 cursor-pointer items-center gap-3 rounded-lg px-2.5 text-[14.5px] text-ink outline-none select-none",
        "data-[selected=true]:bg-surface-hover [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-ink-muted",
      )}
    >
      {children}
    </Command.Item>
  );
}

function PaletteNavItem({ entry, onSelect }: { entry: NavEntry; onSelect: () => void }) {
  const t = useT();
  const allowed = useCan(entry.permission);
  if (!allowed) return null;
  const label = t(entry.labelId);
  return (
    <PaletteItem value={`go ${entry.id} ${label}`} onSelect={onSelect}>
      <entry.icon aria-hidden="true" />
      {label}
    </PaletteItem>
  );
}

/** ⌘K: jump to any section, create anything, or find a lead, unit or deal by name or phone. */
export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const t = useT();
  const navigate = useNavigate();
  const { signOut } = useSession();
  const permissions = usePermissions();

  const run = (action: () => void) => () => {
    onOpenChange(false);
    action();
  };

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-60 animate-fade-in bg-scrim" />
        <RadixDialog.Content
          aria-describedby={undefined}
          className="fixed start-1/2 top-[12vh] z-60 w-[min(640px,calc(100vw-1.5rem))] -translate-x-1/2 animate-pop-in overflow-hidden rounded-2xl border border-line bg-surface shadow-overlay rtl:translate-x-1/2"
        >
          <RadixDialog.Title className="sr-only">{t("palette.title")}</RadixDialog.Title>
          <Command loop className={GROUP}>
            <div className="flex items-center gap-3 border-b border-line px-4">
              <Search className="size-[18px] text-ink-subtle" aria-hidden="true" />
              <Command.Input
                placeholder={t("palette.placeholder")}
                className="h-14 flex-1 bg-transparent text-[16px] text-ink outline-none placeholder:text-ink-subtle"
              />
              <Kbd>Esc</Kbd>
            </div>
            <Command.List className="max-h-[min(58vh,480px)] scroll-py-2 overflow-y-auto p-2">
              <Command.Empty className="px-3 py-10 text-center text-[14px] text-ink-muted">
                {t("palette.empty")}
              </Command.Empty>
              {open ? (
                <PaletteRecords
                  onOpen={(to) => {
                    run(() => void navigate(to))();
                  }}
                />
              ) : null}
              <Command.Group heading={t("palette.navigation")}>
                {NAV_ENTRIES.map((entry) => (
                  <PaletteNavItem
                    key={entry.id}
                    entry={entry}
                    onSelect={run(() => void navigate(entry.to))}
                  />
                ))}
              </Command.Group>
              <Command.Group heading={t("palette.actions")}>
                {CREATE_ACTIONS.filter((action) => permissions.has(action.permission)).map(
                  (action) => (
                    <PaletteItem
                      key={action.id}
                      value={`create ${t(action.labelId)}`}
                      onSelect={run(() => void navigate(createHref(action)))}
                    >
                      <action.icon aria-hidden="true" />
                      {t(action.labelId)}
                    </PaletteItem>
                  ),
                )}
                <PaletteItem value="sign out" onSelect={run(() => void signOut())}>
                  <LogOut className="rtl:-scale-x-100" aria-hidden="true" />
                  {t("shell.signOut")}
                </PaletteItem>
              </Command.Group>
            </Command.List>
            <div className="flex items-center gap-4 border-t border-line bg-surface-subtle px-4 py-2.5 text-[12.5px] text-ink-subtle">
              <span className="flex items-center gap-1.5">
                <Kbd>↑</Kbd>
                <Kbd>↓</Kbd>
                {t("palette.hintMove")}
              </span>
              <span className="flex items-center gap-1.5">
                <Kbd>
                  <CornerDownLeft className="size-3" aria-hidden="true" />
                </Kbd>
                {t("palette.hintOpen")}
              </span>
            </div>
          </Command>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
